import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { useTelegram } from '@/shared/lib/telegram'
import { calculateTotal as calculateTotalPrice } from '@/shared/lib/pricing'
import type { ProductType } from '@/shared/lib/product-type'

// ============================================================
// Типы (соответствуют backend/src/modules/cart/cart.types.ts)
// ============================================================

export type { ProductType }

export interface CartProduct {
  id: string
  name: string
  slug: string
  sku: string | null // Артикул товара
  imageUrl: string | null
  priceAmount: number
  salePriceAmount: number | null
  isOnSale: boolean
  isInStock: boolean
  isCakeAvailable?: boolean
  cakePriceAmount?: number | null
  cakeWeight?: number | null
  isCakeOnly?: boolean
}

export interface CartItem {
  id: string
  userId: number
  productType: ProductType
  productId: string
  quantity: number
  weight: number | null
  createdAt: string
  updatedAt: string
  product?: CartProduct
}

export interface Cart {
  items: CartItem[]
  itemsCount: number
  totalAmount: number
  currency: string
}

interface CartResponse {
  success: boolean
  data: Cart
}

interface CartItemResponse {
  success: boolean
  data: CartItem
}

interface CartCountResponse {
  success: boolean
  data: { count: number }
}

// ============================================================
// Helpers
// ============================================================

function calculateTotal(items: CartItem[]): number {
  return calculateTotalPrice(items)
}

function calculateItemsCount(items: CartItem[]): number {
  return items.reduce((sum, item) => sum + item.quantity, 0)
}

function sortByCreatedAt(items: CartItem[]): CartItem[] {
  return [...items].sort((a, b) => 
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )
}

// ============================================================
// API Hooks
// ============================================================

/**
 * Получить корзину пользователя
 */
export function useCart() {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['cart'],
    queryFn: async () => {
      const response = await apiClient<CartResponse>('/api/user/cart', { initData })
      // Сортируем по дате добавления для стабильного порядка
      if (response.data?.items) {
        response.data.items = sortByCreatedAt(response.data.items)
      }
      return response
    },
    enabled: !!initData,
    staleTime: 30 * 1000, // 30 секунд
  })
}

/**
 * Получить количество товаров в корзине
 */
export function useCartCount() {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['cart', 'count'],
    queryFn: () => apiClient<CartCountResponse>('/api/user/cart/count', { initData }),
    enabled: !!initData,
    staleTime: 30 * 1000,
  })
}

/**
 * Добавить товар в корзину
 * С optimistic update
 */
export function useAddToCart() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: {
      productType: ProductType
      productId: string
      quantity?: number
      weight?: number
    }) => {
      if (!initData) throw new Error('Требуется авторизация')
      
      return apiClient<CartItemResponse>('/api/user/cart', {
        method: 'POST',
        body: JSON.stringify({
          productType: data.productType,
          productId: data.productId,
          quantity: data.quantity ?? 1,
          weight: data.weight,
        }),
        initData,
      })
    },
    onSuccess: (response) => {
      // Обновляем кэш с реальными данными от сервера
      const previousCart = queryClient.getQueryData<CartResponse>(['cart'])
      if (previousCart && response.data) {
        const newItem = response.data
        const existingIndex = previousCart.data.items.findIndex(i => i.id === newItem.id)
        
        let updatedItems: CartItem[]
        if (existingIndex >= 0) {
          // Обновляем существующий
          updatedItems = [...previousCart.data.items]
          updatedItems[existingIndex] = newItem
        } else {
          // Добавляем новый
          updatedItems = [...previousCart.data.items, newItem]
        }
        
        updatedItems = sortByCreatedAt(updatedItems)
        
        queryClient.setQueryData<CartResponse>(['cart'], {
          ...previousCart,
          data: {
            ...previousCart.data,
            items: updatedItems,
            totalAmount: calculateTotal(updatedItems),
            itemsCount: calculateItemsCount(updatedItems),
          },
        })
      } else {
        // Если кэша нет — перезагружаем
        queryClient.invalidateQueries({ queryKey: ['cart'] })
      }
    },
    onError: () => {
      // При ошибке перезагружаем данные
      queryClient.invalidateQueries({ queryKey: ['cart'] })
    },
  })
}

/**
 * Обновить количество товара в корзине
 * С optimistic update
 */
export function useUpdateCartItem() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { itemId: string; quantity: number }) => {
      if (!initData) throw new Error('Требуется авторизация')
      
      return apiClient<CartItemResponse>(`/api/user/cart/${data.itemId}`, {
        method: 'PATCH',
        body: JSON.stringify({ quantity: data.quantity }),
        initData,
      })
    },
    // Optimistic update
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ['cart'] })
      const previousCart = queryClient.getQueryData<CartResponse>(['cart'])
      
      if (previousCart) {
        const updatedItems = previousCart.data.items.map(item => 
          item.id === data.itemId 
            ? { ...item, quantity: data.quantity, updatedAt: new Date().toISOString() }
            : item
        )
        
        queryClient.setQueryData<CartResponse>(['cart'], {
          ...previousCart,
          data: {
            ...previousCart.data,
            items: updatedItems,
            totalAmount: calculateTotal(updatedItems),
            itemsCount: calculateItemsCount(updatedItems),
          },
        })
      }
      
      return { previousCart }
    },
    onError: (_err, _data, context) => {
      // Откатываем при ошибке
      if (context?.previousCart) {
        queryClient.setQueryData(['cart'], context.previousCart)
      }
    },
    // НЕ делаем invalidateQueries в onSettled — optimistic update уже актуален
  })
}

/**
 * Обновить вес товара в корзине (только для чая)
 * С optimistic update
 */
export function useUpdateCartItemWeight() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { itemId: string; weight: number }) => {
      if (!initData) throw new Error('Требуется авторизация')
      
      return apiClient<CartItemResponse>(`/api/user/cart/${data.itemId}/weight`, {
        method: 'PATCH',
        body: JSON.stringify({ weight: data.weight }),
        initData,
      })
    },
    // Optimistic update
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ['cart'] })
      const previousCart = queryClient.getQueryData<CartResponse>(['cart'])
      
      if (previousCart) {
        const updatedItems = previousCart.data.items.map(item => 
          item.id === data.itemId 
            ? { ...item, weight: data.weight, updatedAt: new Date().toISOString() }
            : item
        )
        
        queryClient.setQueryData<CartResponse>(['cart'], {
          ...previousCart,
          data: {
            ...previousCart.data,
            items: updatedItems,
            totalAmount: calculateTotal(updatedItems),
            itemsCount: calculateItemsCount(updatedItems),
          },
        })
      }
      
      return { previousCart }
    },
    onError: (_err, _data, context) => {
      if (context?.previousCart) {
        queryClient.setQueryData(['cart'], context.previousCart)
      }
    },
    // НЕ делаем invalidateQueries — optimistic update уже актуален
  })
}

/**
 * Удалить товар из корзины
 * С optimistic update
 */
export function useRemoveFromCart() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (itemId: string) => {
      if (!initData) throw new Error('Требуется авторизация')
      
      return apiClient(`/api/user/cart/${itemId}`, {
        method: 'DELETE',
        initData,
      })
    },
    // Optimistic update — мгновенно убираем из UI
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: ['cart'] })
      const previousCart = queryClient.getQueryData<CartResponse>(['cart'])
      
      if (previousCart) {
        const updatedItems = previousCart.data.items.filter(item => item.id !== itemId)
        
        queryClient.setQueryData<CartResponse>(['cart'], {
          ...previousCart,
          data: {
            ...previousCart.data,
            items: updatedItems,
            totalAmount: calculateTotal(updatedItems),
            itemsCount: calculateItemsCount(updatedItems),
          },
        })
      }
      
      return { previousCart }
    },
    onError: (_err, _itemId, context) => {
      // Откатываем при ошибке
      if (context?.previousCart) {
        queryClient.setQueryData(['cart'], context.previousCart)
      }
    },
    // НЕ делаем invalidateQueries в onSettled — это создаёт race condition
    // Optimistic update уже отражает реальное состояние
  })
}

/**
 * Очистить корзину
 * С optimistic update
 */
export function useClearCart() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => {
      if (!initData) throw new Error('Требуется авторизация')
      
      return apiClient('/api/user/cart', {
        method: 'DELETE',
        initData,
      })
    },
    // Optimistic update — мгновенно очищаем
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['cart'] })
      const previousCart = queryClient.getQueryData<CartResponse>(['cart'])
      
      if (previousCart) {
        queryClient.setQueryData<CartResponse>(['cart'], {
          ...previousCart,
          data: {
            ...previousCart.data,
            items: [],
            totalAmount: 0,
            itemsCount: 0,
          },
        })
      }
      
      return { previousCart }
    },
    onError: (_err, _vars, context) => {
      if (context?.previousCart) {
        queryClient.setQueryData(['cart'], context.previousCart)
      }
    },
  })
}

/**
 * Проверить, есть ли товар в корзине
 */
export function useIsInCart(productType: ProductType, productId: string) {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['cart', 'check', productType, productId],
    queryFn: () => apiClient<{ success: boolean; data: { inCart: boolean } }>(
      `/api/user/cart/check/${productType}/${productId}`,
      { initData }
    ),
    enabled: !!initData && !!productId,
    staleTime: 30 * 1000,
  })
}

// Re-export для удобства
export type { CartItem as CartItemResponse }
